import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import NumberCardTwo from '../../../../components/Cards/NumberCardTwo'
import { IMAGING_STATUS_COLORS } from '../../../patientRecords/ePCR/Imaging/ImagingRequestsPanel'

const totalLabel = (entry) =>
  `${entry.total} ${entry.unit} over ${entry.count} stud${
    entry.count === 1 ? 'y' : 'ies'
  }${entry.exposures ? ` (${entry.exposures} exposures)` : ''}`

// Imaging studies this employee performed, with radiation dose totals.
// `employee` is the UserAccount doc the page loads - its _id is what
// ImagingRequest.performedBy stores.
const RadiationDosesTab = ({ employee }) => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [summary, setSummary] = useState(null)

  useEffect(() => {
    if (!employee?._id) return
    API.get(`/imaging-doses/user/${employee._id}`)
      .then((res) => {
        if (res.status === 200) setSummary(res.data)
        else setFetchError(true)
      })
      .finally(() => setLoading(false))
  }, [employee?._id])

  const columns = useMemo(
    () => [
      {
        field: 'performedAt',
        headerName: 'Date',
        minWidth: 130,
        valueGetter: (value, row) => moment(row.performedAt).unix(),
        renderCell: ({ row }) =>
          moment(row.performedAt).format('DD/MM/YYYY HH:mm'),
      },
      {
        field: 'modality',
        headerName: 'Modality',
        minWidth: 120,
        valueGetter: (value, row) => row.modality?.label ?? '',
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" height="100%">
            <Chip
              label={row.modality?.label}
              size="small"
              color={row.modality?.ionising ? 'warning' : 'info'}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        ),
      },
      {
        field: 'bodyRegions',
        headerName: 'Body Location',
        minWidth: 160,
        flex: 1,
        valueGetter: (value, row) =>
          [
            (row.bodyRegions || []).join(', '),
            row.laterality && row.laterality !== 'Not Applicable'
              ? row.laterality
              : '',
          ]
            .filter(Boolean)
            .join(' — '),
      },
      {
        field: 'epcrNumber',
        headerName: 'ePCR No',
        minWidth: 100,
        valueGetter: (value, row) => row.epcr?.epcrNumber ?? '',
      },
      {
        field: 'dose',
        headerName: 'Dose',
        minWidth: 130,
        valueGetter: (value, row) =>
          row.dose?.value != null
            ? `${row.dose.value} ${row.dose.unit ?? ''}`
            : row.modality?.ionising
              ? 'Not recorded'
              : '—',
      },
      {
        field: 'exposures',
        headerName: 'Exposures',
        minWidth: 100,
        valueGetter: (value, row) => row.dose?.exposures ?? '',
      },
      {
        field: 'device',
        headerName: 'Device',
        minWidth: 120,
        valueGetter: (value, row) => row.dose?.device ?? '',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 110,
        renderCell: ({ row }) => (
          <Chip
            label={row.status}
            size="small"
            color={IMAGING_STATUS_COLORS[row.status] ?? 'default'}
            sx={{ borderRadius: 1 }}
          />
        ),
      },
    ],
    [],
  )

  if (loading) return <LinearProgress />
  if (fetchError) {
    return <Alert severity="error">Unable to load radiation dose data</Alert>
  }
  if (!summary?.studies?.length) {
    return (
      <Alert severity="info">
        No imaging studies have been recorded as performed by this employee
        yet. Studies are attributed when the clinical impression is recorded
        on an ePCR imaging request.
      </Alert>
    )
  }

  return (
    <Stack gap={2}>
      <Grid container spacing={1}>
        <NumberCardTwo
          number={summary.counts.studiesPerformed}
          label="Studies Performed"
          color={9}
          tooltipTitle="Imaging studies recorded as performed by this employee"
        />
        <NumberCardTwo
          number={summary.counts.ionisingStudies}
          label="Ionising Studies"
          color={summary.counts.ionisingStudies > 0 ? 1 : 4}
          tooltipTitle="Studies using ionising radiation (e.g. X-ray)"
        />
        <NumberCardTwo
          number={summary.counts.dosesRecorded}
          label="Doses Recorded"
          color={3}
          tooltipTitle="Ionising studies with a radiation dose reading"
        />
      </Grid>

      {summary.totals.allTime.length > 0 && (
        <Stack gap={0.5}>
          <Typography variant="title3">Dose Totals</Typography>
          <Typography variant="caption">
            Totals are kept per unit — readings in different units cannot be
            added together.
          </Typography>
          <Stack gap={0.5}>
            <Typography fontWeight="bold" variant="body2">
              Last 12 months
            </Typography>
            {summary.totals.last12Months.length === 0 && (
              <Typography variant="body2">No doses recorded</Typography>
            )}
            {summary.totals.last12Months.map((entry) => (
              <Typography key={entry.unit} variant="body2">
                {totalLabel(entry)}
              </Typography>
            ))}
            <Typography fontWeight="bold" variant="body2">
              All time
            </Typography>
            {summary.totals.allTime.map((entry) => (
              <Typography key={entry.unit} variant="body2">
                {totalLabel(entry)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}

      <CustomDataGrid
        rows={summary.studies}
        columns={columns}
        getRowId={(row) => row._id}
        maxHeight={500}
        hideFilters
        disableRowSelectionOnClick
      />
    </Stack>
  )
}

export default RadiationDosesTab
